import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

export type PromptAnalysisStatus = 'PROCESSING' | 'COMPLETED' | 'FAILED'

export type PromptAnalysis = {
  id: string
  organizationId: string
  agentId: string
  channelId: string | null
  qualifiedDefinition: string | null
  unqualifiedDefinition: string | null
  status: PromptAnalysisStatus
  errorMessage: string | null
  completedAt: string | null
  conversationsAnalyzed: number
  conversationsEvolved: number
  conversationsStalled: number
  model: string
  summary: string
  insights: {
    what_is_converting?: string[]
    unqualified_patterns?: string[]
    what_is_not_working?: string[]
    conversation_could_have_more_results_if?: string[]
    general_improvements?: string[]
    conversation_classifications?: ConversationClassification[]
  }
  createdAt: string
}

export type ConversationClassification = {
  conversationId: string
  customerName: string | null
  outcome: 'EVOLVED' | 'STALLED' | 'INCONCLUSIVE'
  reason: string
  keyMoment: string
  hardSignals: string[]
}

export type OptimizerQuota = {
  canRun: boolean
  blockedReason: string | null
  nextAvailableAt: string | null
  newConversations: number | null
  usedThisMonth: number
  monthlyCap: number
  cooldownHours: number
  minNewConversations: number
}

export type PromptVersion = {
  id: string
  agentId: string
  analysisId: string | null
  sourcePrompt: string
  optimizedPrompt: string
  changeNotes: string | null
  model: string
  appliedAt: string | null
  createdAt: string
}

export type CompatibilitySeverity = 'error' | 'warning' | 'info'

export type CompatibilityFinding = {
  code: string
  severity: CompatibilitySeverity
  category: string
  title: string
  detail: string
  suggestion: string
  fixableByPrompt: boolean
}

export type CompatibilityResult = {
  findings: CompatibilityFinding[]
  checkedAt: string
  llmRan: boolean
}

const optimizerKeys = {
  analyses: (orgId: string | null, agentId: string | null) =>
    ['optimizer', 'analyses', orgId, agentId] as const,
  versions: (orgId: string | null, agentId: string | null) =>
    ['optimizer', 'versions', orgId, agentId] as const,
  quota: (orgId: string | null, agentId: string | null) =>
    ['optimizer', 'quota', orgId, agentId] as const,
  definitionSuggestions: (orgId: string | null, agentId: string | null) =>
    ['optimizer', 'definition-suggestions', orgId, agentId] as const,
}

export type DefinitionSuggestions = {
  qualifiedDefinition: string
  unqualifiedDefinition: string
}

export function useDefinitionSuggestions(
  orgId: string | null,
  agentId: string | null,
  enabled: boolean
) {
  return useQuery<DefinitionSuggestions>({
    queryKey: optimizerKeys.definitionSuggestions(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<DefinitionSuggestions>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/definition-suggestions`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId && enabled,
    staleTime: Infinity,
    retry: 1,
  })
}

export function usePromptCompatibility(
  orgId: string | null,
  agentId: string | null
) {
  return useMutation({
    mutationFn: async (body: { instructions?: string }) => {
      const res = await api.post<CompatibilityResult>(
        `/orgs/${orgId}/agents/${agentId}/prompt-compatibility`,
        body
      )
      return res.data
    },
  })
}

export type CompatibilityFix = {
  correctedPrompt: string
  changes: string[]
}

export function useFixPromptCompatibility(
  orgId: string | null,
  agentId: string | null
) {
  return useMutation({
    mutationFn: async (body: {
      instructions?: string
      findings?: CompatibilityFinding[]
    }) => {
      const res = await api.post<CompatibilityFix>(
        `/orgs/${orgId}/agents/${agentId}/prompt-compatibility/fix`,
        body
      )
      return res.data
    },
  })
}

export function usePromptAnalyses(
  orgId: string | null,
  agentId: string | null
) {
  return useQuery<PromptAnalysis[]>({
    queryKey: optimizerKeys.analyses(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<PromptAnalysis[]>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/analyses`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId,
    staleTime: 30_000,
    // Enquanto houver análise em processamento, acompanha via polling.
    refetchInterval: (query) =>
      query.state.data?.some((a) => a.status === 'PROCESSING') ? 4_000 : false,
  })
}

export function usePromptVersions(
  orgId: string | null,
  agentId: string | null,
  options: { refetchInterval?: number | false } = {}
) {
  return useQuery<PromptVersion[]>({
    queryKey: optimizerKeys.versions(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<PromptVersion[]>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/versions`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId,
    staleTime: 30_000,
    refetchInterval: options.refetchInterval ?? false,
  })
}

export function useOptimizerQuota(
  orgId: string | null,
  agentId: string | null,
  options: { refetchInterval?: number | false } = {}
) {
  return useQuery<OptimizerQuota>({
    queryKey: optimizerKeys.quota(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<OptimizerQuota>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/quota`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId,
    staleTime: 30_000,
    refetchInterval: options.refetchInterval ?? false,
  })
}

export function useRunAnalysis(orgId: string | null, agentId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      channelId?: string | null
      qualifiedDefinition?: string
      unqualifiedDefinition?: string
      limit?: number
    }) => {
      const res = await api.post<PromptAnalysis>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/analyze`,
        body
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: optimizerKeys.analyses(orgId, agentId),
      })
      void qc.invalidateQueries({
        queryKey: optimizerKeys.quota(orgId, agentId),
      })
    },
  })
}

export function useOptimizePrompt(
  orgId: string | null,
  agentId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (analysisId: string) => {
      const res = await api.post<PromptVersion>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/optimize`,
        { analysisId }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: optimizerKeys.versions(orgId, agentId),
      })
    },
  })
}

export function useApplyVersion(orgId: string | null, agentId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (versionId: string) => {
      const res = await api.post<{ ok: boolean }>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/versions/${versionId}/apply`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: optimizerKeys.versions(orgId, agentId),
      })
    },
  })
}

export function useRollbackVersion(
  orgId: string | null,
  agentId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (versionId: string) => {
      const res = await api.post<{ ok: boolean }>(
        `/orgs/${orgId}/agents/${agentId}/optimizer/versions/${versionId}/rollback`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: optimizerKeys.versions(orgId, agentId),
      })
    },
  })
}
